import { ActionIcon, Drawer, Popover, TextInput } from "@mantine/core";
import { useDisclosure }                          from "@mantine/hooks";
import { useEffect, useState }                    from "react";

import { useGetDeviceType } from "_common/hooks/global";
import { PrimaryButton }    from "_components/atoms/button";
import Icon                 from "_components/atoms/icon";
import Typography           from "_components/atoms/typography";
import { isValidCoupon }    from "_services/api/coupons";

import useStyles from "./styles";

export function useProModalScrollLock ( opened ) {
	const deviceType = useGetDeviceType ();

	useEffect ( () => {
		const el = document.getElementById ( "right-side-element" );

		if ( !el )
			return;

		if ( deviceType === "mobile" ) {
			el.style.overflow = "unset";

			return;

		}

		if ( opened ) {
			if ( el ) {
				el.style.overflow = "hidden";

				return;
			}

		}

		el.style.overflow = "auto";

		return () => {
			const el = document.getElementById ( "right-side-element" );

			if ( el )
				el.style.overflow = "auto";

		};
	}, [ opened ] );

	return () => {
		const el = document.getElementById ( "right-side-element" );

		if ( el )
			el.style.overflow = "auto";
	};
}

function CouponPopover ( { packDetails, setCoupon } ) {

	const deviceType                    = useGetDeviceType ();
	const [ opened, { close, toggle } ] = useDisclosure ( false );
	const [ loading, setLoading ]       = useState ( false );

	const [ code, setCode ] = useState ( "" );
	const [ err, setErr ]   = useState ( "" );

	const reset = useProModalScrollLock ( opened );

	const isMobile = deviceType === "mobile";

	function clearCode () {
		setCode ( "" );
		setErr ( "" );
	}

	const handleChangeCode = e => {
		const value = e.currentTarget.value;

		setCode ( value );
	};

	const handleSubmitForm = async e => {
		e.preventDefault ();

		setLoading ( true );

		try {
			const response = await isValidCoupon ( { code: code.toUpperCase (), productId: packDetails?.packId } );

			setCoupon ( response, packDetails );
			toggle ();
			reset ();
		} catch ( err ) {
			setErr ( err.displayMsg );
		}

		setLoading ( false );
	};

	const onClose = () => {
		close ();
		clearCode ();
	};

	return (
		<>
			{!isMobile ? (
				<DesktopComponent
					clearCode = { clearCode }
					close = { onClose }
					code = { code }
					err = { err }
					handleChangeCode = { handleChangeCode }
					handleSubmitForm = { handleSubmitForm }
					loading = { loading }
					opened = { opened }
					toggle = { toggle }
				/>
			)
				: (
					<MobileComponent
						clearCode = { clearCode }
						close = { onClose }
						code = { code }
						err = { err }
						handleChangeCode = { handleChangeCode }
						handleSubmitForm = { handleSubmitForm }
						loading = { loading }
						opened = { opened }
						toggle = { toggle }
					/>
				)}
		</>

	);
}

export default CouponPopover;

function DesktopComponent ( {
	opened,
	toggle,
	close,
	loading,
	clearCode,
	handleSubmitForm,
	handleChangeCode,
	err,
	code
} ) {

	const { classes } = useStyles ();

	return (
		<Popover
			classNames = { {
				dropdown: classes.dropdown
			} }
			onClose = { close }
			opened = { opened }
			position = { "top-start" }
			transitionProps = { { transition: "pop-bottom-left" } }
			withinPortal = { true }
			zIndex = { 1400 }
		>
			<Popover.Target>
				<div onClick = { toggle }>
					<Typography className = { classes.useHereLink }
						size = { "sm" }
						title = { "Use Here" }
					/>
				</div>
			</Popover.Target>

			<Popover.Dropdown>
				<InnerComponent
					clearCode = { clearCode }
					close = { close }
					code = { code }
					err = { err }
					handleChangeCode = { handleChangeCode }
					handleSubmitForm = { handleSubmitForm }
					loading = { loading }
				/>
			</Popover.Dropdown>
		</Popover>
	);
}

function MobileComponent ( {
	code,
	handleChangeCode,
	err,
	handleSubmitForm,
	toggle,
	loading,
	close,
	clearCode,
	opened
} ) {
	const { classes } = useStyles ();

	return (
		<>
			<div onClick = { toggle }>
				<Typography className = { classes.useHereLink }
					size = { "sm" }
					title = { "Use Here" }
				/>
			</div>

			<Drawer
				classNames = { {
					content: classes.drawerContent
				} }
				onClose = { close }
				opened = { opened }
				position = { "bottom" }
				withCloseButton = { false }
				zIndex = { 1400 }
			>
				<InnerComponent
					clearCode = { clearCode }
					close = { close }
					code = { code }
					err = { err }
					handleChangeCode = { handleChangeCode }
					handleSubmitForm = { handleSubmitForm }
					loading = { loading }
				/>
			</Drawer>
		</>
	);
}

function InnerComponent ( { close, handleSubmitForm, clearCode, handleChangeCode, code, err, loading } ) {
	const { classes, cx } = useStyles ();
	const deviceType      = useGetDeviceType ();

	return (
		<>
			<div className = { cx ( classes.padding, classes.header ) }>
				<Typography title = { "Use Promo Code" }
					weight = { 500 }
				/>

				<ActionIcon className = { classes.closeIconWrapper }
					onClick = { close }
				>
					<Icon name = { "crossWhite" }
						sameInBothTheme = { true }
						size = { "2rem" }
					/>
				</ActionIcon>
			</div>

			<div className = { cx ( classes.inputWrapper ) }>
				<form onSubmit = { handleSubmitForm }>
					<TextInput
						classNames = { {
							root         : classes.inputRoot,
							error        : classes.error,
							input        : classes.input,
							wrapper      : classes.inputWrapper,
							rightSection : classes.rightSection
						} }
						error = { err }
						onChange = { handleChangeCode }
						placeholder = { "Enter your promo code here" }
						rightSection =
							{ code ? (
								<Icon
									className = { classes.crossIcon }
									name = { "crossWhite" }
									onClick = { clearCode }
									sameInBothTheme = { true }
								/>
							) : null }
						value = { code }
					/>
				</form>
			</div>

			<div className = { cx ( classes.padding, classes.footer ) }>
				{/* <Button className = { classes.cancelBtn }
					onClick = { close }
					size = { "md" }
					variant = { "default" }
				>
					{"Cancel"}
				</Button> */}

				<PrimaryButton
					className = { classes.submitBtn }
					disabled = { code === "" }
					fullWidth = { deviceType === "mobile" }
					loading = { loading }
					onClick = { handleSubmitForm }
					title = { "Apply" }
				/>
			</div>
		</>
	);
}
